import React, { FC, useRef, useEffect } from 'react'
import { Series } from '../Charts/Series'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import { useId } from '@reach/auto-id'
import { XYChart } from '@amcharts/amcharts4/charts'
import { useRefWithUpdate } from 'hooks/useRefWithUpdate'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import { transformData } from 'components/Charts/common'

am4core.useTheme(am4themes_animated)

type BarChartProps = {
  data: Series
  selected?: string[]
  onSelect?: (index: string[]) => void
}

export const BarChart: FC<BarChartProps> = ({ data, selected = [], onSelect }) => {
  const chartId = useId()
  const chartRef = useRef<XYChart>()
  const transformedData = transformData(data)
  const filters = useRefWithUpdate<string[]>(selected)

  const handleSelection = (value: string) => {
    if (!onSelect) return
    const isSelected = filters.current.includes(value)

    const newSelected = isSelected
      ? filters.current.filter((filter) => value !== filter)
      : [...filters.current, value]

    const allSelected = newSelected.length === data.keys.length
    allSelected ? onSelect([]) : onSelect(newSelected)
  }

  // -- Setup chart
  useEffect(() => {
    const chart = am4core.create(chartId, am4charts.XYChart)

    chart.data = transformedData
    chart.zoomOutButton.disabled = true
    chart.colors.list = data.colors[0].map((c) => am4core.color(c))

    createAxes(chart)

    data.values.forEach((_value, index) => {
      createSeries(chart, 'values', index, handleSelection, filters.current, data.colors, false)

    })

    chartRef.current = chart

    // Remove ugly amCharts icon xD (
    chart.events.on('validated', () => {

      chart.logo.visible = false;

      //document.querySelector(`g[opacity^="0.4"`)?.remove()
    })

    return () => {
      chartRef.current?.dispose()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId, `lenght-${data.labels.length}`])

  // Update selection
  useEffect(() => {
    if (!chartRef.current) return

    chartRef.current.series.each((serie) => {
      ; (serie as am4charts.ColumnSeries).columns.values.forEach((column) => {
        const opacity =
          column.dataItem && selected.includes(column.dataItem.categories.categoryY) ? 1 : 0.6
        column.fillOpacity = opacity


        //column.stroke = am4core.color("#1cffe5", 0.8);

        //column.strokeWidth = column.dataItem && selected.includes(column.dataItem.categories.categoryY) ? 1 : 0
      })
    })
  }, [selected, chartRef])

  // -- Update data
  useEffect(() => {
    if (!chartRef.current) return

    transformedData.forEach((_, index) => {
      const item = chartRef.current!.data[index]
      Object.keys(item).forEach((key) => {
        item[key] = transformedData[index][key]
      })
    })

    //    
    chartRef.current.data = transformedData;
    chartRef.current.series.clear();
    chartRef.current.colors.list = data.colors[0].map((c) => am4core.color(c))
    data.values.forEach((_value, index) => {
      createSeries(chartRef.current ? chartRef.current : am4core.create(chartId, am4charts.XYChart), 'values', index, handleSelection, filters.current, data.colors, true)

    })
    // eslint-disable-next-line
  }, [transformedData]) // eslint-disable-next-line

  let chartHeight = transformedData.length * 20;

  if (chartHeight < 200) {
    chartHeight = 200;
  }
  return <div id={chartId} style={{ width: '100%', height: chartHeight }}></div>
}

const createAxes = (chart: XYChart) => {
  const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
  categoryAxis.dataFields.category = 'label'
  categoryAxis.renderer.cellStartLocation = 0.1
  categoryAxis.renderer.cellEndLocation = 0.9
  categoryAxis.renderer.grid.template.opacity = 0

  categoryAxis.renderer.marginLeft = -10
  categoryAxis.renderer.fontSize = 12
  categoryAxis.renderer.minGridDistance = 10
  categoryAxis.renderer.minLabelPosition = 0
  categoryAxis.renderer.labels.template.maxWidth = 150
  categoryAxis.renderer.labels.template.truncate = true
  categoryAxis.renderer.labels.template.tooltipText = '{category}'
  categoryAxis.renderer.labels.template.tooltipX = 210
  categoryAxis.renderer.labels.template.tooltipPosition = "fixed"
  categoryAxis!.tooltip!.label.maxWidth = 180
  categoryAxis!.tooltip!.label.textAlign = "end"
  //categoryAxis!.tooltip!.label.align = "right"
  categoryAxis!.tooltip!.label.wrap = true

  const valueAxis = chart.xAxes.push(new am4charts.ValueAxis())
  valueAxis.min = 0
  valueAxis.renderer.line.stroke = am4core.color('#cccccc')
  valueAxis.renderer.line.strokeOpacity = 1
  valueAxis.renderer.fontSize = 12
  valueAxis.height = 12
}

const createSeries = (
  chart: XYChart,
  field: string,
  index: number,
  handleSelection: (a: string) => void,
  filters: string[],
  colores: string[][],
  animateSeries: boolean,
) => {
  const series = chart.series.push(new am4charts.ColumnSeries())
  // Binding data to the series
  series.dataFields.valueX = `${field}${index}`
  series.dataFields.categoryY = 'label'

  series.propertyFields.fill = `colors${index}`

  if (animateSeries === false) {
    series.showOnInit = false
  }
  var contador = -1;
  series.columns.template.adapter.add("fill", (fill, target) => {
    contador += 1
    var ac = am4core.color(colores[0][contador]);
    return ac;
  });
  // Animations
  series.sequencedInterpolation = true
  series.interpolationDuration = 500

  series.calculatePercent = true;

  //series.columns.template.tooltipText = "[bold]{name}[/]\n[font-size:14px]{categoryY}: [bold]{valueX.percent}%[/] ({valueX}M)";

  //var valueLabel = series.bullets.push(new am4charts.LabelBullet());
  //valueLabel.label.text = "{valueX.percent.formatNumber(\"#.##\")}%";
  //valueLabel.label.fontSize = 12;
  //valueLabel.label.horizontalCenter = "left";
  //valueLabel.label.dx = 10;
  //valueLabel.label.truncate = false;
  //valueLabel.label.hideOversized = false;
  var valueLabel = series.columns.template.createChild(am4core.Label);
  valueLabel.text = "{valueX.percent.formatNumber(\"#.#\")}%";
  valueLabel.fontSize = 12;
  valueLabel.valign = "middle";
  valueLabel.dx = 5;

  series.columns.template.strokeWidth = 0
  series.columns.template.height = am4core.percent(100)

  // Tooltips with format

  // eslint-disable-next-line
  series.columns.template.tooltipText = '{categoryY}: [bold]{valueX}[/] {valueX.percent.formatNumber(\"#.##\")}%'
  //series.columns.template.tooltipText = '{categoryY}: [bold]{valueX}'

  series.columns.template.tooltipX = am4core.percent(100)

  if (series.tooltip) {
    series.tooltip.getFillFromObject = false
    series.tooltip.background.fill = am4core.color('#FFF')
    series.tooltip.background.fillOpacity = 1
    series.tooltip.label.fill = am4core.color('#777')
    series.tooltip.dx = 5
  }

  // Events
  series.columns.template.events.on('hit', (event) => {

    let dataconte = event.target.dataItem ? JSON.stringify(event.target.dataItem.dataContext) : "";
    let contex = JSON.parse(dataconte)
    handleSelection(contex["key"])

  })

  series.events.on('ready', (ev) => {
    ev.target.columns.each((column) => {
      column.fillOpacity =
        column.dataItem && filters.includes(column.dataItem.categories.categoryY) ? 1 : 0.6

      // column.stroke = am4core.color("#1cffe5", 0.8);
      // column.strokeWidth = column.dataItem && filters.includes(column.dataItem.categories.categoryY) ? 1 : 0

    })
  })
}
